// ─── PWA Push Helper ──────────────────────────────────────────────────────────
// Client-side utilities for:
//   1. Registering the Service Worker
//   2. Requesting push notification permission
//   3. Creating a PushSubscription and sending it to the server

const VAPID_PUBLIC_KEY = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY ?? "";

/** Convert a base64url string to a Uint8Array (required for VAPID public key) */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

/** Sufixo do URL do worker sem intermediação de `fetch` — ver `public/sw.js`. */
export const SW_SEM_FETCH = "semfetch=1";

/**
 * Register the Service Worker. Returns the registration or null on failure.
 *
 * `semFetch` pede a variante que não intercepta pedidos: é a que se regista em
 * desenvolvimento, onde o `fetch` do worker parte as route storyboards do
 * Tempo e o cache só serviria builds velhos. O push funciona igual nas duas.
 */
export async function registerServiceWorker(
  { semFetch = false }: { semFetch?: boolean } = {},
): Promise<ServiceWorkerRegistration | null> {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return null;
  try {
    // A chave VAPID viaja no URL do worker.
    //
    // O `sw.js` é um ficheiro estático de `public/`: não vê `process.env`, e
    // precisa da chave para se voltar a subscrever sozinho quando o browser lhe
    // roda a subscrição (`pushsubscriptionchange`). O `scope` é dado à parte e
    // continua a ser "/", portanto a query não cria um registo novo — mas muda
    // o URL do script, o que faz o browser reinstalar o worker quando a chave
    // mudar, que é exactamente quando é preciso.
    const params = new URLSearchParams();
    if (VAPID_PUBLIC_KEY) params.set("vapid", VAPID_PUBLIC_KEY);
    if (semFetch) params.set("semfetch", "1");
    const query = params.toString();
    const url = query ? `/sw.js?${query}` : "/sw.js";

    const registration = await navigator.serviceWorker.register(url, {
      scope: "/",
    });
    return registration;
  } catch (err) {
    console.error("[SW] Registration failed:", err);
    return null;
  }
}

/**
 * Porque é que a subscrição não se fez.
 *
 * Existe porque `false` não chegava. Havia seis maneiras diferentes de esta
 * função falhar e todas chegavam à interface como o mesmo nada: a pessoa
 * carregava em "Activar", não acontecia coisa nenhuma, e nem ela nem quem
 * recebia o relato tinha forma de saber se o problema era a permissão, o
 * endereço, o service worker ou o servidor. Um botão que falha em silêncio
 * ensina a não voltar a carregar nele.
 */
export type PushSubscribeReason =
  /** O browser não tem API de push — ou o site não está em https:// nem em localhost. */
  | "sem-suporte"
  /** Falta NEXT_PUBLIC_VAPID_PUBLIC_KEY no ambiente. Problema de configuração, não do utilizador. */
  | "sem-chave"
  /** A pessoa recusou explicitamente. Só se reverte nas definições do site. */
  | "recusada"
  /** A caixa apareceu e foi fechada sem responder — a permissão fica em "default". */
  | "ignorada"
  /** O service worker nunca ficou activo. É o caso que antes ficava pendurado para sempre. */
  | "sem-service-worker"
  /** A subscrição saiu sem chaves — raro, e não há nada a fazer do lado de cá. */
  | "sem-chaves"
  /**
   * O browser tem a API de push mas não tem serviço de push por trás dela.
   * É o caso dos browsers embutidos em aplicações Electron — o do Tempo, o do
   * Slack, a vista de um e-mail —, que trazem o Chromium sem a ligação ao
   * serviço da Google. Sem este motivo, a falha caía em "erro" e dizia «tente
   * de novo» a quem podia tentar mil vezes.
   */
  | "sem-servico"
  /** O `/api/push/subscribe` recusou. O aviso local funciona; o remoto não. */
  | "servidor"
  | "erro";

export interface PushSubscribeResult {
  ok: boolean;
  reason?: PushSubscribeReason;
  /** Detalhe técnico para a consola, nunca para o ecrã. */
  detail?: string;
}

/**
 * `navigator.serviceWorker.ready` **nunca rejeita**: se não houver registo
 * activo para este âmbito, fica à espera para sempre. Era o pendurar mais
 * provável deste fluxo — o botão ficava em "A activar…" até a página
 * recarregar. Dez segundos é muito mais do que um registo já feito demora, e
 * muito menos do que a paciência de quem está a olhar para um botão.
 */
async function readyServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>((resolve) => setTimeout(() => resolve(null), 10_000)),
  ]);
}

/**
 * Request push notification permission and subscribe.
 *
 * Devolve sempre o motivo quando falha — ver `PushSubscribeReason`.
 */
export async function subscribeToPush(userId: string): Promise<PushSubscribeResult> {
  if (typeof window === "undefined") return { ok: false, reason: "erro" };

  // Os dois numa verificação só de propósito: num contexto inseguro (http://
  // que não seja localhost) o Chrome não expõe nem `PushManager` nem
  // `Notification`, e o sintoma é indistinguível de um browser antigo. A
  // mensagem tem de falar do endereço, que é o que a pessoa pode mudar.
  if (!("PushManager" in window) || !("Notification" in window)) {
    return {
      ok: false,
      reason: "sem-suporte",
      // `globalThis` e não `window`: o `in` acima estreita o tipo de `window`
      // e o TypeScript deixa de lhe reconhecer os membros nesta ramificação.
      detail: globalThis.isSecureContext
        ? "PushManager/Notification em falta"
        : "contexto inseguro — a origem não é https:// nem localhost",
    };
  }

  if (!VAPID_PUBLIC_KEY) {
    console.warn("[Push] VAPID public key not configured.");
    return { ok: false, reason: "sem-chave" };
  }

  try {
    // 1. Ask for permission
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      // Fechar a caixa sem responder deixa a permissão em "default" — e era
      // este o caso que se lia como "não aconteceu nada".
      return { ok: false, reason: permission === "denied" ? "recusada" : "ignorada" };
    }

    // 2. Get SW registration
    const registration = await readyServiceWorker();
    if (!registration) {
      return { ok: false, reason: "sem-service-worker" };
    }

    // 3. Subscribe via PushManager
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY).buffer as ArrayBuffer,
    });

    // 4. Extract keys from subscription
    const p256dhBuffer = subscription.getKey("p256dh");
    const authBuffer = subscription.getKey("auth");
    if (!p256dhBuffer || !authBuffer) return { ok: false, reason: "sem-chaves" };

    const p256dh = btoa(String.fromCharCode(...new Uint8Array(p256dhBuffer)));
    const auth = btoa(String.fromCharCode(...new Uint8Array(authBuffer)));

    // 5. Send subscription to server
    const res = await fetch("/api/push/subscribe", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        user_id: userId,
        endpoint: subscription.endpoint,
        p256dh,
        auth,
      }),
    });

    if (!res.ok) {
      // Desfaz a subscrição do browser antes de reportar a falha.
      //
      // Sem isto os dois lados ficavam a discordar, e a discordância é pior do
      // que a falha: o browser ficava subscrito, o servidor sem linha para onde
      // empurrar, e — como o painel se desenha a partir do browser — passava a
      // anunciar «Notificações do navegador activas» a quem não ia receber
      // nenhuma. Um erro que se vê tem remédio; um estado que mente, não.
      try {
        await subscription.unsubscribe();
      } catch {
        /* Se nem desfazer se consegue, a mensagem de falha é o que resta. */
      }
      return { ok: false, reason: "servidor", detail: `HTTP ${res.status}` };
    }
    return { ok: true };
  } catch (err) {
    console.error("[Push] Subscription error:", err);

    // O Chromium recusa com `AbortError` quando não tem serviço de push ligado
    // — a mensagem exacta ("push service not available") não é garantida entre
    // versões, mas o par nome+passo é: aqui, um `AbortError` do `subscribe()`
    // só pode vir daí, porque a permissão já foi concedida acima.
    if (err instanceof DOMException && err.name === "AbortError") {
      return { ok: false, reason: "sem-servico", detail: String(err) };
    }

    return { ok: false, reason: "erro", detail: String(err) };
  }
}

/**
 * Este dispositivo está subscrito?
 *
 * **Não é a mesma pergunta que a permissão.** A permissão é do navegador e
 * fica concedida para sempre até a pessoa a mexer nas definições do site; a
 * subscrição é desta aplicação e liga-se e desliga-se à vontade. Depois de
 * alguém carregar em "Desativar", a permissão continua `granted` e a
 * subscrição não existe — e é a segunda que diz se os avisos chegam.
 *
 * É por isso que o painel não pode desenhar-se só a partir de
 * `Notification.permission`: mostraria "activas" a quem as acabou de
 * desligar.
 */
export async function isSubscribedToPush(): Promise<boolean | null> {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return false;
  if (!("Notification" in window)) return false;
  if (Notification.permission !== "granted") return false;

  try {
    // `getRegistration()` primeiro, e `ready` só como recurso.
    //
    // `ready` espera por um worker **activo**; o registo existe muito antes
    // disso, e o `pushManager` é do registo, não do worker. Bastava o worker
    // estar a instalar-se ou a actualizar-se — ou a linha principal estar
    // ocupada, que numa página cheia é o normal — para esta pergunta demorar
    // mais do que os 10s do `readyServiceWorker`, e uma subscrição bem viva
    // ser lida como inexistente.
    const registration =
      (await navigator.serviceWorker.getRegistration()) ??
      (await readyServiceWorker());

    // `null` e não `false`: **não saber não é o mesmo que estar desligado.**
    // Era esta a raiz do sintoma — o `readyServiceWorker` devolve `null` ao fim
    // de 10s por desistência, e essa desistência chegava à interface como uma
    // afirmação («as notificações estão desligadas») que ninguém tinha
    // verificado. Quem tem a permissão concedida e uma subscrição viva via um
    // botão «Activar» a propor-lhe o que já tinha. Com `null`, a interface não
    // desenha nem um estado nem o outro até saber qual é.
    if (!registration) return null;

    return (await registration.pushManager.getSubscription()) !== null;
  } catch {
    return null;
  }
}

/**
 * Desliga as notificações deste dispositivo.
 *
 * Dois passos, e os dois são precisos: cancelar a subscrição no navegador (o
 * serviço de push deixa de a aceitar) e apagar a linha da base (o servidor
 * deixa de tentar). Só o primeiro deixaria o servidor a empurrar para um
 * endpoint morto até apanhar um 410; só o segundo deixaria o navegador
 * subscrito a um remetente que já não escreve.
 *
 * A permissão do navegador fica como estava — revogá-la não é coisa que uma
 * página possa fazer, e é por isso que o texto do botão fala de avisos e não
 * de permissões.
 */
export async function unsubscribeFromPush(): Promise<boolean> {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return false;

  try {
    const registration = await readyServiceWorker();
    const subscription = await registration?.pushManager.getSubscription();
    if (!subscription) return true; // já estava desligado

    const { endpoint } = subscription;
    await subscription.unsubscribe();

    const res = await fetch("/api/push/subscribe", {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ endpoint }),
    });

    return res.ok;
  } catch (err) {
    console.error("[Push] Unsubscribe error:", err);
    return false;
  }
}

/** O que se diz à pessoa por cada motivo. Uma frase, e com saída quando há. */
export const PUSH_REASON_TEXT: Record<PushSubscribeReason, string> = {
  "sem-suporte":
    "Este endereço não permite notificações. Abra o Zunox em https:// (ou em localhost) e tente de novo.",
  "sem-chave":
    "As notificações não estão configuradas neste servidor. Fale com quem administra o Zunox.",
  recusada:
    "O navegador bloqueou o pedido. Reactive-as nas definições do site, no cadeado da barra de endereço.",
  ignorada:
    "A caixa do navegador foi fechada sem resposta. Carregue outra vez e escolha «Permitir».",
  "sem-service-worker":
    "O navegador não conseguiu preparar as notificações. Recarregue a página e tente de novo.",
  "sem-chaves": "O navegador devolveu uma subscrição incompleta. Tente noutro navegador.",
  "sem-servico":
    "Este navegador não tem serviço de notificações. Abra o Zunox no Chrome ou no Edge — janelas dentro de outra aplicação não as recebem.",
  servidor: "O servidor recusou a subscrição. Tente de novo dentro de momentos.",
  erro: "Não foi possível activar. Tente de novo.",
};

/**
 * Se este servidor tem sequer chave VAPID.
 *
 * `NEXT_PUBLIC_*` é fixado no BUILD, não lido em execução: um ambiente onde a
 * variável não estava definida quando se compilou produz um pacote em que
 * isto é `false` para sempre, e nenhuma subscrição é possível até se voltar a
 * compilar. Sabê-lo antes de desenhar o ecrã é o que evita oferecer um botão
 * que não pode funcionar — e pedir a alguém que carregue num botão morto é
 * pior do que não lho mostrar.
 */
export function isPushConfigured(): boolean {
  return Boolean(VAPID_PUBLIC_KEY);
}

/** Returns the current notification permission state */
export function getNotificationPermission(): NotificationPermission | "unsupported" {
  if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
  return Notification.permission;
}
